import React from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { Shield, AlertCircle, Lock } from "lucide-react";

/**
 * Protection Modal Component
 * Modal for selecting protection methods and applying protection
 */
export const ProtectionModal = ({
  isOpen,
  isLoading,
  selectedColumnsCount,
  selectedProtectionMethod,
  protectionMethods,
  protectionResult,
  isApplying,
  previewMode,
  onMethodSelect,
  onApplyProtection,
  onDownloadProtectedData,
  onClose,
}) => {
  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full pointer-events-auto flex flex-col"
          style={{ maxHeight: "calc(100vh - 120px)" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6 text-purple-600" />
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Select Protection Method
                </h3>
                <p className="text-sm text-gray-600">
                  {selectedColumnsCount} column
                  {selectedColumnsCount > 1 ? "s" : ""} will be protected
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4 overflow-y-auto flex-1">
            {/* Protection Methods */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Protection Method
                </label>
                {protectionMethods.map((method) => (
                  <div
                    key={method.id || method.name}
                    onClick={() =>
                      onMethodSelect(method.id || method.name.toLowerCase())
                    }
                    className={`border-2 rounded-xl p-4 cursor-pointer transition-all ${selectedProtectionMethod ===
                      (method.id || method.name.toLowerCase())
                      ? "border-purple-600 bg-purple-50 shadow-md"
                      : "border-gray-200 hover:border-purple-300 hover:bg-gray-50"
                      }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="radio"
                        checked={
                          selectedProtectionMethod ===
                          (method.id || method.name.toLowerCase())
                        }
                        onChange={() => undefined}
                        className="w-5 h-5 text-purple-600 border-gray-300 focus:ring-purple-500 mt-0.5"
                      />
                      <div className="flex-1">
                        <h4 className="text-sm font-semibold text-gray-800 mb-1">
                          {method.name || method.id}
                        </h4>
                        <p className="text-xs text-gray-600">
                          {method.description ||
                            `Apply ${method.name || method.id} to protect sensitive data`}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Protection Result */}
            {protectionResult && (
              <div
                className={`border-2 rounded-xl p-4 ${protectionResult.success
                  ? "border-green-200 bg-green-50"
                  : "border-red-200 bg-red-50"
                  }`}
              >
                <div className="flex items-start gap-3">
                  {protectionResult.success ? (
                    <svg className="w-6 h-6 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p
                      className={`text-sm font-semibold ${protectionResult.success ? "text-green-800" : "text-red-800"}`}
                    >
                      {protectionResult.message}
                    </p>
                    {protectionResult.success && protectionResult.results && (
                      <div className="mt-3 space-y-2">
                        {protectionResult.results.map((result, idx) => (
                          <button
                            key={idx}
                            onClick={() => onDownloadProtectedData(result, idx)}
                            className="text-xs text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download Protected Data {idx + 1}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-all font-medium"
            >
              Cancel
            </button>
            <button
              onClick={onApplyProtection}
              disabled={!selectedProtectionMethod || isApplying}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
            >
              {isApplying ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  {previewMode ? "Generate Preview" : "Apply Protection"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

ProtectionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  selectedColumnsCount: PropTypes.number.isRequired,
  selectedProtectionMethod: PropTypes.string,
  protectionMethods: PropTypes.arrayOf(PropTypes.object).isRequired,
  protectionResult: PropTypes.shape({
    success: PropTypes.bool,
    message: PropTypes.string,
    results: PropTypes.array,
    failed: PropTypes.array,
  }),
  isApplying: PropTypes.bool.isRequired,
  previewMode: PropTypes.bool.isRequired,
  onMethodSelect: PropTypes.func.isRequired,
  onApplyProtection: PropTypes.func.isRequired,
  onDownloadProtectedData: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
